import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { stripePromise } from '../config/stripe';
import './SuccessPage.css';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentIntent, setPaymentIntent] = useState(null);
  // const [error, setError] = useState(null);

  // Get payment data passed from CartPage
  const { paymentData } = location.state || {};

  useEffect(() => {
    const checkPaymentStatus = async () => {
      // If we have payment data from navigation state, use it
      if (paymentData) {
        setPaymentIntent({
          id: paymentData.paymentIntentId,
          status: paymentData.status,
          amount: paymentData.amount,
          currency: 'gbp'
        });
        setPaymentStatus('succeeded');
        return;
      }
      
      const clientSecret = searchParams.get('payment_intent_client_secret');
      
      if (!clientSecret) {
        // No payment intent, show default success
        setPaymentStatus('default');
        return;
      }

      try {
        const stripe = await stripePromise;
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        
        setPaymentIntent(paymentIntent);
        
        switch (paymentIntent.status) {
          case 'succeeded':
            setPaymentStatus('succeeded');
            break;
          case 'processing':
            setPaymentStatus('processing');
            break;
          case 'requires_payment_method':
            setPaymentStatus('failed');
            break;
          default:
            setPaymentStatus('unknown');
        }
      } catch (err) {
        console.error('Error retrieving payment intent:', err);
        // setError(err.message);
        setPaymentStatus('error');
      }
    };

    checkPaymentStatus();
  }, [searchParams, paymentData]);

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'GBP',
    }).format(amount / 100);
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'loading':
        return (
          <div className="success-container">
            <div className="success-content">
              <div className="loading-spinner"></div>
              <h1 className="success-title">Processing Payment...</h1>
              <p className="success-message">
                Please wait while we confirm your payment.
              </p>
            </div>
          </div>
        );

      case 'succeeded':
        return (
          <div className="success-container">
            <div className="success-content">
              <div className="success-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#00d924" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="#00d924" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <h1 className="success-title">Payment Successful! 🎉</h1>
              
              <p className="success-message">
                Thank you for your order! Your payment has been processed successfully.
                We'll start preparing your delicious cake right away.
              </p>
              
              <div className="success-info">
                <div className="info-item">
                  <h3>Payment Details</h3>
                  <div className="payment-details">
                    <p><strong>Payment ID:</strong> {paymentIntent?.id}</p>
                    <p><strong>Amount:</strong> {formatAmount(paymentIntent?.amount, paymentIntent?.currency)}</p>
                    <p><strong>Status:</strong> <span className="status-success">✅ Paid</span></p>
                  </div>
                </div>
                <div className="info-item">
                  <h3>What's Next?</h3>
                  <ul>
                    <li>You'll receive a confirmation email shortly</li>
                    <li>We'll contact you about collection/delivery details</li>
                    <li>Your cake will be freshly prepared</li>
                  </ul>
                </div>
              </div>
              
              <div className="success-actions">
                <Link to="/menu" className="success-button primary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.3 5.1 16.3H17M17 13V17C17 18.1 17.9 19 19 19S21 17.9 21 17V13M9 19.5C9.8 19.5 10.5 20.2 10.5 21S9.8 22.5 9 22.5 7.5 21.8 7.5 21 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21S20.8 22.5 20 22.5 18.5 21.8 18.5 21 19.2 19.5 20 19.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Continue Shopping
                </Link>
                
                <Link to="/" className="success-button secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9L12 2L21 9V20C21 20.5 20.5 21 20 21H4C3.5 21 3 20.5 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="success-container">
            <div className="success-content">
              <div className="processing-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#ff9500" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="#ff9500" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              
              <h1 className="success-title">Payment Processing ⏳</h1>
              
              <p className="success-message">
                Your payment is being processed. This may take a few moments.
              </p>
              
              <div className="success-info">
                <div className="info-item">
                  <h3>Payment Details</h3>
                  <div className="payment-details">
                    <p><strong>Payment ID:</strong> {paymentIntent?.id}</p>
                    <p><strong>Amount:</strong> {formatAmount(paymentIntent?.amount, paymentIntent?.currency)}</p>
                    <p><strong>Status:</strong> <span className="status-processing">⏳ Processing</span></p>
                  </div>
                  <p>You will receive an email confirmation once the payment is complete.</p>
                </div>
              </div>
              
              <div className="success-actions">
                <Link to="/" className="success-button primary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="success-container">
            <div className="success-content">
              <div className="error-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
                  <path d="M15 9L9 15M9 9L15 15" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              
              <h1 className="success-title">Payment Failed ❌</h1>
              
              <p className="success-message">
                Unfortunately, your payment was not successful. Please try again.
              </p>
              
              <div className="success-info">
                <div className="info-item">
                  <h3>Payment Details</h3>
                  <div className="payment-details">
                    <p><strong>Payment ID:</strong> {paymentIntent?.id}</p>
                    <p><strong>Status:</strong> <span className="status-failed">❌ Failed</span></p>
                  </div>
                </div>
              </div>
              
              <div className="success-actions">
                <Link to="/menu" className="success-button primary">
                  Try Again
                </Link>
                <Link to="/" className="success-button secondary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="success-container">
            <div className="success-content">
              <div className="success-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#00d924" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="#00d924" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <h1 className="success-title">Welcome to Panela Crumbs! 🎂</h1>
              
              <p className="success-message">
                Thank you for visiting our bakery. Discover our delicious Venezuelan-inspired cakes and desserts.
              </p>
              
              <div className="success-actions">
                <Link to="/menu" className="success-button primary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.3 5.1 16.3H17M17 13V17C17 18.1 17.9 19 19 19S21 17.9 21 17V13M9 19.5C9.8 19.5 10.5 20.2 10.5 21S9.8 22.5 9 22.5 7.5 21.8 7.5 21 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21S20.8 22.5 20 22.5 18.5 21.8 18.5 21 19.2 19.5 20 19.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  View Our Menu
                </Link>
                
                <Link to="/" className="success-button secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9L12 2L21 9V20C21 20.5 20.5 21 20 21H4C3.5 21 3 20.5 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="success-page">
      {renderContent()}
    </div>
  );
};

export default SuccessPage;